import _ from 'lodash';
import { getPrice } from './getPrice';
import { formatPrice } from './formatPrice';
import { originDischargeSectionArr } from './originDischargeSectionArr';
import { calculateLocalCharge } from './ecuNormalizationCharge';
import { CHARGE_ROW } from './getOriginDestinationCharges';

/**
 * @memberof module:util
 * @function
 * @name getOriginDischargeData
 * @description It provides total price for origin and discharge view-details options,
 * whether origin or discharge option should be hidden or not,
 * table data array for origin/discharge tables in FCL/LCL/AIR types.
 *
 * @param {string} mode - it's either 'origin' or 'discharge'
 * @param {Object} itemJson - Single rate card
 * @param {number} chargeableWeight - Either it's the chargeable weight for FCL/LCL/AIR or
 * the number of containers in a single container type for FCL.
 * @param {boolean} [isDeliveryChecked = undefined] - Whether delivery checkbox is checked or not
 * @returns {Object}
 *
 * @example
 * console.log(getOriginDischargeData(...)); //{modeIsHidden: false, rate: 394.84, tableDataArr:[...]}
 */

// mode is origin or discharge
//for origin or discharge table data
// chargeableWeight for fcl or lcl or air
//array item index 2 has boolean value, make it dynamic or static.
//if it's true then multiply by by # of containers (FCL) or chargable weight (LCL/Air)
//if it's false, then it's static one charge per booking

//return an object
export const getOriginDischargeData = (
  mode,
  itemJson,
  chargeableWeight,
  isDeliveryChecked = undefined,
  options = {}
) => {
  const tableDataObj = getTableData(
    mode,
    itemJson,
    chargeableWeight,
    isDeliveryChecked,
    options
  );

  // console.log('tableDataObj: ', tableDataObj);

  return tableDataObj;
};

// get all options
const getTableData = (
  mode,
  itemJson,
  chargeableWeight,
  isDeliveryChecked,
  options = {}
) => {
  const { wtInKG, volInCBM } = options;
  // const modeSection = getSection(mode);
  let modeIsHidden = false;
  let rateArr = [];
  let tableDataArr = [];

  let modeSectionArr = originDischargeSectionArr(
    mode,
    itemJson,
    isDeliveryChecked,
    options
  );

  if (Array.isArray(modeSectionArr) && modeSectionArr.length !== 0) {
    rateArr = modeSectionArr.map((item) => {
      if (!Array.isArray(item)) return 0;
      let tempRate;
      const unitCode = item[3];
      const calculationCode = item[6];
      const quantity = item[7];
      // console.log("calculationCode: ",calculationCode, "unitCode: ",unitCode, "charge code: ",item[0],
      //   "ratePerUnit: ",item[2]
      // );
      if (calculationCode) {
        // ECU API charge: use the full calculation engine (minimum is applied internally)
        // Use Number() instead of getPrice() to preserve sub-cent precision (e.g. 0.114 not 0.11)
        tempRate = calculateLocalCharge(
          {
            calculationCode,
            quantity,
            ratePerUnit: Number(item[2]),
            minimumUSD: Number(item[4]) || 0,
          },
          wtInKG || 0,
          volInCBM || 0
        );
        // console.log("tempRate: ",tempRate, "chargeableWeight: ",chargeableWeight);
      } else {
        // Standard charge: apply unitCode-based multiplier
        tempRate = Number(item[2]);
        // console.log("tempRate: ",tempRate, "chargeableWeight: ",chargeableWeight);
        if (unitCode !== 'EA' && unitCode !== 'shipment') {
          tempRate = tempRate * chargeableWeight;
        }

        // for minimum rate, define min. check after multiply check
        if (item[4]) {
          let minPrice = Number(item[4]);
          tempRate = tempRate < minPrice ? minPrice : tempRate;
        }
      }

      // if chargeableWeight is zero, when user has not input anything
      // EA and shipment unit codes are flat per-booking charges — not weight/volume dependent
      if (chargeableWeight === 0 && unitCode !== 'EA' && unitCode !== 'shipment') {
        tempRate = 0;
      }

      tempRate = Number(tempRate);
      // format rates in tableDataArr for displaying only.
      // 4th element carries the per-row estimate flag so the table can badge it (index 8 of the
      // source row). Legacy/short rows have no index 8 → undefined → not estimated.
      tableDataArr.push([item[0], item[1], formatPrice(tempRate), item[CHARGE_ROW.IS_ESTIMATE] === true]);
      return tempRate;
    });
  } else {
    modeIsHidden = true;
  }

  let rate = _.sum(rateArr); //total rate i.e. summation of rates in tableDataArr
  rate = getPrice(rate);

  return { modeIsHidden, rate, tableDataArr };
};

// // get section arr
// const getSectionArr = (mode, itemJson, isDeliveryChecked) => {
//   const modeSection = getSection(mode);
//   let sectionArr =
//     (Array.isArray(itemJson[modeSection]) && itemJson[modeSection]) || [];

//   if (!isDeliveryChecked && mode === DISCHARGE) {
//     let extraChargeArr = itemJson[DISCHARGE_EXTRA_CHARGE_JSON_KEY];
//     extraChargeArr =
//       Array.isArray(extraChargeArr) && extraChargeArr.length !== 0
//         ? extraChargeArr
//         : [];
//     // add extra charge array to discharge section arr
//     extraChargeArr.map((item) => sectionArr.push(item));
//   }

//   return sectionArr;
// };

// // get section
// const getSection = (mode) => {
//   let modeSection;
//   if (mode === ORIGIN) {
//     modeSection = ORIGIN_JSON_KEY;
//   } else if (mode === DISCHARGE) {
//     modeSection = DISCHARGE_JSON_KEY;
//   }
//   return modeSection;
// };

// // mode
// const ORIGIN = 'origin';
// const DISCHARGE = 'discharge';

// // for json, mode section
// const ORIGIN_JSON_KEY = 'port_fees_from';
// const DISCHARGE_JSON_KEY = 'port_fees_to';

// // destination or discharge extra charges
// const DISCHARGE_EXTRA_CHARGE_JSON_KEY = 'destination_extra_charges';
